var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Collapse, Link, Stack, styled } from '@mui/material';
import { useContext, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';
import * as yup from 'yup';
import { Autocomplete, ErrorMessageBox, FormField, InputLabel } from '../../common';
import { SelectOrCustomField } from '../../common/selectOrCustomField';
import { LocationRoleType } from '../../orgConfigWizard/staffing/selectors';
import { ALL_LOCATIONS_ID, MAX_TEXT_LENGTH } from '../constants';
import { WizardContext } from '../context';
import { useQueueAPI } from '../queue/selectors';
import { useIsPlanLocked, useLocationOptions, useMultiLangTranslations, useQueueLocations, useWizardParams } from '../selectors';
import { useAddRoleAPI, useRoleAppliesToOptions, useRolePlanOptionsAPI } from './selectors';
import { RoleAppliesTo } from './types';
var PREDEFINED_ROLE_TYPES = [LocationRoleType.Supervisor, LocationRoleType.QualityAssurance, LocationRoleType.Trainer, LocationRoleType.WfmAnalyst, LocationRoleType.Recruiter];
var ROLE_TYPES = __spreadArray(__spreadArray([], PREDEFINED_ROLE_TYPES, true), [LocationRoleType.Custom], false);
var ROLE_TRANS_KEYS = (_a = {}, _a[LocationRoleType.Supervisor] = 'supportStaff.types.supervisor', _a[LocationRoleType.QualityAssurance] = 'supportStaff.types.qa', _a[LocationRoleType.Trainer] = 'supportStaff.types.trainer', _a[LocationRoleType.WfmAnalyst] = 'supportStaff.types.wfmAnalyst', _a[LocationRoleType.Recruiter] = 'supportStaff.types.recruiter', _a[LocationRoleType.Custom] = 'supportStaff.types.custom', _a);
var isRoleType = function (role) {
  return ROLE_TYPES.includes(role);
};
var getPlanRoleNames = function (options) {
  return Array.from(new Set((options !== null && options !== void 0 ? options : []).map(function (option) {
    var _a;
    return (_a = option.role) === null || _a === void 0 ? void 0 : _a.trim();
  }).filter(function (role) {
    return !!role;
  })));
};
var getCustomRoleOptions = function (options, translatedRoleNames) {
  return getPlanRoleNames(options).filter(function (role) {
    return !isRoleType(role) && !translatedRoleNames.has(role);
  }).sort(function (left, right) {
    return left.localeCompare(right);
  }).map(function (role) {
    return {
      id: role,
      name: role
    };
  });
};
var initialValues = {
  roleType: LocationRoleType.Supervisor,
  customName: '',
  queueLocationId: null,
  roleAppliesTo: RoleAppliesTo.Queue
};
var RootBox = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    marginTop: theme.spacing(6)
  };
});
var FormContainer = styled('form')(function (_a) {
  var theme = _a.theme;
  return {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(6),
    marginTop: theme.spacing(6),
    backgroundColor: theme.palette.grey[200]
  };
});
var AddRoleFormDetails = function (_a) {
  var control = _a.control,
    onSubmit = _a.onSubmit,
    onOpenForm = _a.onOpenForm,
    loading = _a.loading,
    errors = _a.errors,
    roleType = _a.roleType,
    setValue = _a.setValue,
    watch = _a.watch,
    roleOptions = _a.roleOptions;
  var t = useTranslation(['wizard', 'translation']).t;
  var _b = useToggle(),
    showForm = _b[0],
    toggle = _b[1];
  var locations = useQueueLocations();
  var locationOptions = useLocationOptions();
  var roleAppliesToOptions = useRoleAppliesToOptions();
  var hasLocations = !!locations.length;
  var isPlanLocked = useIsPlanLocked();
  var isCustom = roleType === LocationRoleType.Custom;
  var handleToggle = function () {
    if (!showForm) {
      onOpenForm();
    }
    toggle();
  };
  return _jsxs(RootBox, {
    children: [_jsx(Link, __assign({
      component: "button",
      onClick: handleToggle,
      disabled: isPlanLocked
    }, {
      children: t('supportStaff.addRole')
    })), _jsx(Collapse, __assign({
      in: showForm
    }, {
      children: _jsxs(FormContainer, __assign({
        onSubmit: onSubmit,
        "data-testid": "addRoleForm"
      }, {
        children: [_jsx(ErrorMessageBox, {
          errors: errors,
          scrollTop: false
        }), _jsxs(Stack, __assign({
          direction: "row",
          sx: {
            alignItems: 'flex-start',
            justifyItems: 'center'
          },
          spacing: 2
        }, {
          children: [_jsxs(Box, __assign({
            flex: 1,
            maxWidth: "16rem"
          }, {
            children: [_jsx(InputLabel, __assign({
              required: true
            }, {
              children: t('supportStaff.roleName')
            })), _jsx(SelectOrCustomField, {
              required: true,
              isCustom: isCustom,
              control: control,
              name: "roleType",
              customName: "customName",
              options: roleOptions,
              defaultValue: LocationRoleType.Supervisor,
              placeholder: t('translation:layout.pleaseSelect'),
              customPlaceholder: t('supportStaff.enterCustomRole'),
              customValue: watch('customName'),
              onCustomChange: function (value) {
                return setValue('customName', value);
              },
              onSelectChange: function (value) {
                if (value) setValue('roleType', value);
              },
              maxLength: MAX_TEXT_LENGTH,
              disabled: isPlanLocked
            })]
          })), hasLocations && _jsxs(Box, __assign({
            flex: 1,
            maxWidth: "16rem"
          }, {
            children: [_jsx(InputLabel, __assign({
              required: true
            }, {
              children: t('supportStaff.siteLocationName')
            })), _jsx(FormField, __assign({
              required: true,
              control: control,
              name: "queueLocationId"
            }, {
              children: _jsx(Autocomplete, {
                options: locationOptions,
                placeholder: t('translation:layout.pleaseSelect'),
                size: "small",
                disabled: isPlanLocked
              })
            }))]
          })), _jsxs(Box, __assign({
            flex: 1,
            maxWidth: "12rem"
          }, {
            children: [_jsx(InputLabel, __assign({
              required: true
            }, {
              children: t('supportStaff.roleAppliesTo')
            })), _jsx(FormField, __assign({
              required: true,
              control: control,
              name: "roleAppliesTo"
            }, {
              children: _jsx(Autocomplete, {
                disabled: !hasLocations || isPlanLocked,
                options: roleAppliesToOptions,
                placeholder: t('translation:layout.pleaseSelect'),
                size: "small"
              })
            }))]
          })), _jsxs(Box, {
            children: [_jsx(InputLabel, __assign({
              sx: {
                visibility: 'hidden'
              }
            }, {
              children: '\u00A0'
            })), _jsx(LoadingButton, __assign({
              disabled: isPlanLocked,
              type: "submit",
              variant: "outlined",
              loading: loading
            }, {
              children: t('translation:action.add')
            }))]
          })]
        }))]
      }))
    }))]
  });
};
export var AddRoleForm = function () {
  var _a = useTranslation('wizard'),
    t = _a.t,
    i18n = _a.i18n;
  var queueId = useWizardParams().queueId;
  var _b = useQueueAPI(),
    fetchQueue = _b[1];
  var _c = useAddRoleAPI(),
    addRoleData = _c[0],
    addRole = _c[1],
    clearAddRole = _c[2];
  var _d = useRolePlanOptionsAPI(),
    planOptionsData = _d[0],
    fetchPlanOptions = _d[1];
  var locations = useQueueLocations();
  var validateForm = yup.object().shape(__assign({
    roleType: yup.string().required(),
    customName: yup.string().when('roleType', {
      is: LocationRoleType.Custom,
      then: function (schema) {
        return schema.required().max(35);
      },
      otherwise: function (schema) {
        return schema.notRequired();
      }
    })
  }, locations.length ? {
    queueLocationId: yup.number().nullable().required().label('Site Location Name'),
    roleAppliesTo: yup.string().required().label('Role applies to')
  } : {}));
  var defaultValues = __assign(__assign({}, initialValues), {
    queueLocationId: locations.length ? ALL_LOCATIONS_ID : null
  });
  var _e = useForm({
      defaultValues: defaultValues,
      resolver: yupResolver(validateForm)
    }),
    control = _e.control,
    handleSubmit = _e.handleSubmit,
    reset = _e.reset,
    watch = _e.watch,
    setValue = _e.setValue;
  var roleType = watch('roleType');
  var _f = useContext(WizardContext),
    handleSave = _f.handleSave,
    isDirty = _f.methods.formState.isDirty;
  var roleI18nKeys = useMemo(function () {
    return PREDEFINED_ROLE_TYPES.map(function (type) {
      return ROLE_TRANS_KEYS[type];
    });
  }, []);
  var translatedRoleNames = useMultiLangTranslations(i18n, roleI18nKeys, 'wizard');
  var roleOptions = useMemo(function () {
    var predefinedOptions = ROLE_TYPES.map(function (type) {
      return {
        id: type,
        name: t(ROLE_TRANS_KEYS[type])
      };
    });
    var customRoleOptions = getCustomRoleOptions(planOptionsData.data, translatedRoleNames);
    var customOption = predefinedOptions.at(-1);
    return customOption ? __spreadArray(__spreadArray(__spreadArray([], predefinedOptions.slice(0, -1), true), customRoleOptions, true), [customOption], false) : __spreadArray(__spreadArray([], predefinedOptions, true), customRoleOptions, true);
  }, [planOptionsData.data, t, translatedRoleNames]);
  useEffect(function () {
    if (addRoleData.data) {
      reset(defaultValues);
      fetchQueue();
      fetchPlanOptions();
    }
    return clearAddRole;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRoleData.data, clearAddRole, fetchPlanOptions, fetchQueue, reset]);
  var getRoleName = function (values) {
    if (values.roleType === LocationRoleType.Custom) {
      return values.customName.trim();
    }
    if (isRoleType(values.roleType)) {
      return t(ROLE_TRANS_KEYS[values.roleType]);
    }
    return values.roleType;
  };
  var handleAddRole = handleSubmit(function (values) {
    var roleName = getRoleName(values);
    var payload = {
      queueId: queueId,
      role: roleName,
      queueLocationId: values.queueLocationId,
      roleAppliesTo: values.roleAppliesTo
    };
    if (isDirty) {
      return handleSave('submit', function () {
        return addRole(payload);
      })();
    }
    addRole(payload);
  });
  return _jsx(AddRoleFormDetails, {
    control: control,
    setValue: setValue,
    watch: watch,
    roleType: roleType,
    roleOptions: roleOptions,
    onOpenForm: fetchPlanOptions,
    onSubmit: handleAddRole,
    loading: addRoleData.requesting,
    errors: [addRoleData.error]
  });
};